import { ref, readonly } from 'vue';
import { isElectron } from '../config/runtime';

export type ElectronUpdateStatus =
  'idle' | 'checking' | 'available' | 'downloading' | 'downloaded' | 'up-to-date' | 'error';

export interface ElectronDownloadProgress {
  percent: number;
  transferred: number;
  total: number;
  bytesPerSecond: number;
}

export interface ElectronUpdateCheckResult {
  ok: boolean;
  currentVersion?: string;
  updateAvailable?: boolean;
  version?: string | null;
  error?: string;
}

interface ElectronUpdateAPI {
  isElectron?: boolean;
  onUpdateAvailable?: (cb: () => void) => void;
  onUpdateNotAvailable?: (cb: () => void) => void;
  onUpdateDownloaded?: (cb: () => void) => void;
  onUpdateError?: (cb: (message: string) => void) => void;
  onDownloadProgress?: (cb: (progress: ElectronDownloadProgress) => void) => void;
  getAppVersion?: () => Promise<string>;
  checkForUpdates?: () => Promise<ElectronUpdateCheckResult>;
  quitAndInstallUpdate?: () => void;
}

// Module-level singleton state so the banner and the Settings page share one
// view of the update lifecycle.
const status = ref<ElectronUpdateStatus>('idle');
const progress = ref(0);
const transferredBytes = ref(0);
const totalBytes = ref(0);
const errorMessage = ref('');
const updateVersion = ref<string | null>(null);
const appVersion = ref<string | null>(null);

let subscribed = false;
let versionLoaded = false;

function getAPI(): ElectronUpdateAPI | undefined {
  return window.electron;
}

async function loadAppVersion(): Promise<void> {
  if (versionLoaded) return;
  const api = getAPI();
  if (!isElectron() || !api?.getAppVersion) return;
  versionLoaded = true;
  try {
    appVersion.value = await api.getAppVersion();
  } catch {
    // Non-fatal — the version display is optional.
  }
}

function subscribe(): void {
  const api = getAPI();
  if (!isElectron() || !api || subscribed) return;
  subscribed = true;

  api.onUpdateAvailable?.(() => {
    status.value = 'downloading';
  });
  api.onUpdateNotAvailable?.(() => {
    status.value = 'up-to-date';
  });
  api.onUpdateDownloaded?.(() => {
    status.value = 'downloaded';
  });
  api.onUpdateError?.((message) => {
    status.value = 'error';
    errorMessage.value = message;
  });
  api.onDownloadProgress?.((p) => {
    status.value = 'downloading';
    progress.value = Math.round(p.percent || 0);
    transferredBytes.value = p.transferred || 0;
    totalBytes.value = p.total || 0;
  });
}

export function useElectronUpdates() {
  subscribe();
  loadAppVersion();

  async function checkForUpdates(): Promise<void> {
    const api = getAPI();
    if (!isElectron() || !api?.checkForUpdates) return;
    status.value = 'checking';
    errorMessage.value = '';
    try {
      const result = await api.checkForUpdates();
      if (!result.ok) {
        status.value = 'error';
        errorMessage.value = result.error || 'Update check failed';
      } else if (result.updateAvailable) {
        status.value = 'downloading';
        updateVersion.value = result.version || null;
      } else {
        status.value = 'up-to-date';
        updateVersion.value = result.version || null;
      }
    } catch (__caught: unknown) {
      const err = __caught as Error;
      status.value = 'error';
      errorMessage.value = err?.message || String(err);
    }
  }

  function quitAndInstall(): void {
    getAPI()?.quitAndInstallUpdate?.();
  }

  return {
    isElectron: isElectron(),
    status: readonly(status),
    progress: readonly(progress),
    transferredBytes: readonly(transferredBytes),
    totalBytes: readonly(totalBytes),
    errorMessage: readonly(errorMessage),
    updateVersion: readonly(updateVersion),
    appVersion: readonly(appVersion),
    checkForUpdates,
    quitAndInstall,
  };
}
